import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, ApiError } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { CompanyStatusBadge } from '../components/StatusBadge.tsx';
import { Avatar } from '../components/Avatar.tsx';
import { StatusFilterTabs } from '../components/StatusFilterTabs.tsx';
import { NavIcon, type IconName } from '../components/NavIcon.tsx';
import { ConfirmModal } from '../components/ConfirmModal.tsx';
import { Modal } from '../components/Modal.tsx';
import { Field, Select, TextInput } from '../components/Field.tsx';
import { MultiSelect } from '../components/MultiSelect.tsx';
import { SearchableMultiSelect } from '../components/SearchableMultiSelect.tsx';
import { StringListEditor } from '../components/StringListEditor.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { usePortalSettings } from '../lib/portalSettings.tsx';
import {
  clusterRegionOptions,
  companyInfoLabel,
  contractorTypeOptions,
  stationOptions,
} from '../lib/companyInfo.ts';
import {
  configuredFieldValues,
  countByStatus,
  filterCompanies,
  parseFieldFilterParam,
  parseStatusFilterParam,
  type StatusFilter,
} from '../lib/companyFilter.ts';
import { paginate } from '../lib/pagination.ts';
import type { Company } from '../lib/types.ts';
import { buildVendorCsv } from '../lib/vendorCsv.ts';
import { resendActionForStatus } from '../lib/resendAction.ts';

const PAGE_SIZES = [15, 25, 50, 100, 200, 300] as const;
const DEFAULT_PAGE_SIZE = 15;
const PAGE_SIZE_STORAGE_KEY = 'vendors.pageSize';

// Remembered rows-per-page preference: an explicit ?size= in the URL wins (so a
// shared/bookmarked link is exact), else the last choice saved to localStorage,
// else the default (15).
function readStoredPageSize(): number {
  try {
    const n = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
    return (PAGE_SIZES as readonly number[]).includes(n) ? n : DEFAULT_PAGE_SIZE;
  } catch {
    return DEFAULT_PAGE_SIZE;
  }
}

export function AdminCompanies() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () => adminApi.get<{ companies: Company[] }>('/api/companies'),
  });

  const { settings } = usePortalSettings();

  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [filter, setFilter] = useState(() => searchParams.get('q') ?? '');
  // Keep the search box in sync when ?q= changes in the URL without a remount
  // (e.g. the sidebar "Vendors" link resets the query while already on the list).
  // Typing flows local→URL via setSearch; this covers the URL→local direction.
  useEffect(() => {
    setFilter(searchParams.get('q') ?? '');
  }, [searchParams]);

  // Page number + search text live in the URL (alongside the status/field
  // filters) so the browser Back button — and the detail page's "← Vendors"
  // link — restore the exact page and filters the admin left off on.
  const page = Math.max(1, Math.floor(Number(searchParams.get('page')) || 1));
  const setPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next <= 1) p.delete('page');
        else p.set('page', String(next));
        return p;
      },
      { replace: true },
    );
  };

  // Rows per page: URL ?size= wins, else the localStorage preference, else 15.
  const sizeParam = Number(searchParams.get('size'));
  const pageSize = (PAGE_SIZES as readonly number[]).includes(sizeParam)
    ? sizeParam
    : readStoredPageSize();
  const setPageSize = (next: number) => {
    try {
      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(next));
    } catch {
      /* ignore — private mode / storage disabled */
    }
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === DEFAULT_PAGE_SIZE) p.delete('size');
        else p.set('size', String(next));
        p.delete('page'); // a size change resets to the first page
        return p;
      },
      { replace: true },
    );
  };

  // Keep the search box in local state (snappy typing, no cursor jumps) but
  // mirror it to ?q= so it survives navigation; a new search resets to page 1.
  const setSearch = (next: string) => {
    setFilter(next);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next.trim() === '') p.delete('q');
        else p.set('q', next);
        p.delete('page');
        return p;
      },
      { replace: true },
    );
  };

  const statusFilter = parseStatusFilterParam(searchParams.get('status'));
  const setStatusFilter = (next: StatusFilter) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'all') p.delete('status');
        else p.set('status', next);
        p.delete('page'); // a filter change resets to the first page
        return p;
      },
      { replace: true },
    );
  };

  // Contractor type, cluster region and station options are curated in Settings
  // (settings) — same source as the create-company modal, in configured order.
  const typeValues = useMemo(
    () =>
      configuredFieldValues(
        contractorTypeOptions(settings),
        data?.companies ?? [],
        'contractor_types',
      ),
    [settings, data],
  );
  const regionValues = useMemo(
    () =>
      configuredFieldValues(
        clusterRegionOptions(settings),
        data?.companies ?? [],
        'cluster_region',
      ),
    [settings, data],
  );
  const stationValues = useMemo(
    () => configuredFieldValues(stationOptions(settings), data?.companies ?? [], 'station'),
    [settings, data],
  );

  // Field filters are multi-select: each URL key is repeatable
  // (?type=a&type=b&region=x&station=y). Read every value with getAll and
  // validate against the current option pool.
  const typeFilter = parseFieldFilterParam(searchParams.getAll('type'), typeValues);
  const regionFilter = parseFieldFilterParam(searchParams.getAll('region'), regionValues);
  const stationFilter = parseFieldFilterParam(searchParams.getAll('station'), stationValues);

  // Rewrite a repeatable field-filter param: drop every existing entry for the
  // key, then append one per selected value (empty selection = no key).
  const setFieldFilter = (key: 'type' | 'region' | 'station', next: string[]) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete(key);
        for (const v of next) p.append(key, v);
        p.delete('page'); // a filter change resets to the first page
        return p;
      },
      { replace: true },
    );
  };

  const companies = useMemo(
    () =>
      filterCompanies(data?.companies ?? [], {
        query: filter,
        status: statusFilter,
        contractorType: typeFilter,
        clusterRegion: regionFilter,
        station: stationFilter,
      }),
    [data, filter, statusFilter, typeFilter, regionFilter, stationFilter],
  );

  // Status pill counts are faceted: they reflect every active filter EXCEPT
  // status itself (a facet never filters its own counts — otherwise clicking a
  // pill would zero all the others). 'any' (not 'all') so deactivated rows
  // reach countByStatus and the Deactivated pill shows a real count — 'all'
  // would strip them and pin that pill to 0.
  const { counts, total: facetedTotal } = useMemo(
    () =>
      countByStatus(
        filterCompanies(data?.companies ?? [], {
          query: filter,
          status: 'any',
          contractorType: typeFilter,
          clusterRegion: regionFilter,
          station: stationFilter,
        }),
      ),
    [data, filter, typeFilter, regionFilter, stationFilter],
  );
  // Deactivated companies are parked — the page's headline total reflects only
  // active companies (the Deactivated pill carries its own count).
  const grandTotal = useMemo(
    () => (data?.companies ?? []).filter((c) => c.status !== 'deactivated').length,
    [data],
  );

  const filtersActive =
    filter.trim() !== '' ||
    statusFilter !== 'all' ||
    typeFilter.length > 0 ||
    regionFilter.length > 0 ||
    stationFilter.length > 0;

  // Each filter setter already resets to page 1 (it drops the ?page= param), so
  // no post-render page-reset effect is needed.
  // One setSearchParams call clears all the filter params: react-router's
  // functional updater reads the *location's* current params, so chained calls
  // in the same tick would clobber each other.
  const clearFilters = () => {
    setFilter('');
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete('status');
        p.delete('type');
        p.delete('region');
        p.delete('station');
        p.delete('q');
        p.delete('page');
        return p;
      },
      { replace: true },
    );
  };

  const [invitedCompany, setInvitedCompany] = useState<Company | null>(null);
  const inviteMutation = useMutation({
    mutationFn: (id: string) =>
      adminApi.post<{ sent: boolean; expires_at: string }>(`/api/companies/${id}/invite`),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['companies'] });
      setInvitedCompany(data?.companies.find((c) => c.id === id) ?? null);
    },
  });

  const resendLoginMutation = useMutation({
    mutationFn: (id: string) =>
      adminApi.post<{ sent: boolean }>(`/api/companies/${id}/resend-login-link`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
  });

  const [confirmAction, setConfirmAction] = useState<{
    company: Company;
    action: 'deactivate' | 'reactivate';
  } | null>(null);

  const deactivateMutation = useMutation({
    mutationFn: (id: string) =>
      adminApi.post<{ company: Company }>(`/api/companies/${id}/deactivate`),
    onSuccess: () => {
      setConfirmAction(null);
      void qc.invalidateQueries({ queryKey: ['companies'] });
    },
  });
  const reactivateMutation = useMutation({
    mutationFn: (id: string) =>
      adminApi.post<{ company: Company }>(`/api/companies/${id}/reactivate`),
    onSuccess: () => {
      setConfirmAction(null);
      void qc.invalidateQueries({ queryKey: ['companies'] });
    },
  });
  // The modal's confirm button drives whichever mutation matches the pending action.
  const confirmMutation =
    confirmAction?.action === 'reactivate' ? reactivateMutation : deactivateMutation;

  // Export the currently-filtered vendors (all pages, not just the visible
  // page) to a CSV the browser downloads. Client-side: the full list is already
  // loaded, so no API round-trip is needed. `new Date()` is fine in the browser.
  function exportCsv() {
    const csv = buildVendorCsv(companies);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendors-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  if (isLoading) return <CenteredSpinner />;

  const {
    pageItems,
    page: currentPage,
    totalPages,
    from,
    to,
    total,
  } = paginate(companies, page, pageSize);

  // Stash the current list URL (page + filters) so the detail page's "← Vendors"
  // link can return the admin to exactly this view.
  const listSearch = searchParams.toString();

  return (
    <div className="space-y-5">
      {/* Title row: heading + subtitle on the left, primary action top-right. */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Vendors</h1>
          <p className="mt-1 text-sm text-slate-500" aria-live="polite">
            {filtersActive
              ? `${companies.length} of ${grandTotal} ${grandTotal === 1 ? 'vendor' : 'vendors'}`
              : `${grandTotal} total ${grandTotal === 1 ? 'vendor' : 'vendors'}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Btn variant="info" onClick={exportCsv} disabled={companies.length === 0}>
            Export CSV
          </Btn>
          <Btn onClick={() => setCreateOpen(true)}>+ New vendor</Btn>
        </div>
      </div>

      {/* Status filter pills — replaces the old dropdown. */}
      <StatusFilterTabs
        value={statusFilter}
        counts={counts}
        total={facetedTotal}
        onChange={setStatusFilter}
      />

      {/* Companies card: the search row sits at the top of the same card as the
          table (per the screenshot), so it stays visible even when no rows match
          and the user needs to clear the search. */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 p-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
                <NavIcon name="search" size={18} />
              </span>
              <TextInput
                className="pl-10"
                placeholder="Search vendor name, contact, vendor # or contract #…"
                aria-label="Search vendors"
                value={filter}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <SearchableMultiSelect
              label={companyInfoLabel(settings, 'contractorType')}
              unitLabel="contractor types"
              values={typeValues.values}
              selected={typeFilter}
              includeNotSet
              onChange={(next) => setFieldFilter('type', next)}
            />
            <SearchableMultiSelect
              label={companyInfoLabel(settings, 'clusterRegion')}
              unitLabel="cluster regions"
              values={regionValues.values}
              selected={regionFilter}
              includeNotSet
              onChange={(next) => setFieldFilter('region', next)}
            />
            <SearchableMultiSelect
              label={companyInfoLabel(settings, 'station')}
              unitLabel="stations"
              values={stationValues.values}
              selected={stationFilter}
              includeNotSet
              onChange={(next) => setFieldFilter('station', next)}
            />
          </div>
        </div>

        {companies.length === 0 ? (
          // Borderless empty block (mirrors EmptyState's content) so it sits
          // inside the card rather than drawing its own dashed border.
          <div className="p-12 text-center">
            <h3 className="text-base font-semibold text-slate-900">
              {filtersActive ? 'No matching vendors' : 'No vendors yet'}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {filtersActive
                ? 'No vendors match your current filters.'
                : 'Add a vendor to invite them to complete the questionnaire.'}
            </p>
            <div className="mt-4 flex justify-center">
              {filtersActive ? (
                <Btn variant="secondary" onClick={clearFilters}>
                  Clear filters
                </Btn>
              ) : (
                <Btn onClick={() => setCreateOpen(true)}>+ New vendor</Btn>
              )}
            </div>
          </div>
        ) : (
          // The columns overflow narrow viewports and the card clips
          // (overflow-hidden), so let the table scroll sideways instead of
          // crushing the cells.
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-3">Vendor</th>
                  <th className="px-6 py-3">Contact</th>
                  {/* Tenant-renamable labels (Settings), same source as the
                      filter dropdowns above. */}
                  <th className="px-6 py-3">{companyInfoLabel(settings, 'contractorType')}</th>
                  <th className="px-6 py-3">{companyInfoLabel(settings, 'clusterRegion')}</th>
                  <th className="px-6 py-3">{companyInfoLabel(settings, 'station')}</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pageItems.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar name={c.company_name} />
                        <Link
                          to={`/admin/companies/${c.id}`}
                          state={{ vendorsListSearch: listSearch ? `?${listSearch}` : '' }}
                          className="font-medium text-slate-900 hover:underline"
                        >
                          {c.company_name}
                        </Link>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {c.contact_name}
                      <div className="text-xs text-slate-500">{c.email}</div>
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {c.contractor_types && c.contractor_types.length > 0 ? (
                        c.contractor_types.join(', ')
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {c.cluster_region ?? <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {c.station ?? <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-6 py-4">
                      <CompanyStatusBadge status={c.status} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        {c.status === 'deactivated' ? (
                          <IconActionBtn
                            label="Reactivate vendor"
                            icon="rotate-ccw"
                            onClick={() => setConfirmAction({ company: c, action: 'reactivate' })}
                          />
                        ) : (
                          <>
                            {resendActionForStatus(c.status, c.invited_at ?? null) === 'invite' ? (
                              <IconActionBtn
                                label={c.invited_at ? 'Resend invite' : 'Send invite'}
                                icon="mail"
                                loading={
                                  inviteMutation.isPending && inviteMutation.variables === c.id
                                }
                                onClick={() => inviteMutation.mutate(c.id)}
                              />
                            ) : (
                              <IconActionBtn
                                label="Resend login link"
                                icon="mail"
                                loading={
                                  resendLoginMutation.isPending &&
                                  resendLoginMutation.variables === c.id
                                }
                                onClick={() => resendLoginMutation.mutate(c.id)}
                              />
                            )}
                            <IconActionBtn
                              label="Deactivate vendor"
                              icon="ban"
                              onClick={() => setConfirmAction({ company: c, action: 'deactivate' })}
                            />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {total > 0 && (
          <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3 text-slate-500">
              <span>
                Showing {from}–{to} of {total}
              </span>
              <label className="flex items-center gap-1.5">
                <span>Per page</span>
                <Select
                  className="!w-auto"
                  aria-label="Rows per page"
                  value={String(pageSize)}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
              </label>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Btn
                  size="sm"
                  variant="secondary"
                  disabled={currentPage <= 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  Previous
                </Btn>
                <span className="text-slate-500">
                  Page {currentPage} of {totalPages}
                </span>
                <Btn
                  size="sm"
                  variant="secondary"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                </Btn>
              </div>
            )}
          </div>
        )}
      </div>

      {inviteMutation.error && <ErrorMessage error={inviteMutation.error} />}

      <Modal
        open={invitedCompany !== null}
        onClose={() => setInvitedCompany(null)}
        title="Invitation sent"
        size="sm"
        footer={
          <Btn variant="info" onClick={() => setInvitedCompany(null)}>
            Done
          </Btn>
        }
      >
        <p className="text-sm text-slate-600">
          An invitation email has been sent to {invitedCompany?.email ?? 'the vendor contact'}.
        </p>
      </Modal>

      {confirmAction && (
        <ConfirmModal
          open
          title={confirmAction.action === 'deactivate' ? 'Deactivate vendor' : 'Reactivate vendor'}
          confirmLabel={confirmAction.action === 'deactivate' ? 'Deactivate' : 'Reactivate'}
          danger={confirmAction.action === 'deactivate'}
          loading={confirmMutation.isPending}
          onConfirm={() => confirmMutation.mutate(confirmAction.company.id)}
          onClose={() => {
            setConfirmAction(null);
            deactivateMutation.reset();
            reactivateMutation.reset();
          }}
        >
          {confirmAction.action === 'deactivate' ? (
            <>
              Are you sure you want to deactivate{' '}
              <span className="font-semibold">{confirmAction.company.company_name}</span>?
              They&apos;ll be removed from the vendors list and lose portal access. Their records
              are kept — you can reactivate them anytime.
            </>
          ) : (
            <>
              Are you sure you want to reactivate{' '}
              <span className="font-semibold">{confirmAction.company.company_name}</span>?
              They&apos;ll return to their previous status and regain portal access.
            </>
          )}
          {confirmMutation.error != null && (
            <div className="mt-3">
              <ErrorMessage error={confirmMutation.error} />
            </div>
          )}
        </ConfirmModal>
      )}
      <CreateCompanyModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

// Compact icon-only action for table rows. Btn's sm padding is sized for text,
// so square it up with an important px override; the label reaches assistive
// tech via aria-label and sighted users via the title tooltip.
function IconActionBtn({
  label,
  icon,
  onClick,
  loading = false,
}: {
  label: string;
  icon: IconName;
  onClick: () => void;
  loading?: boolean;
}) {
  return (
    <Btn
      size="sm"
      variant="ghost"
      className="!px-2"
      aria-label={label}
      title={label}
      loading={loading}
      onClick={onClick}
    >
      {loading ? null : <NavIcon name={icon} size={16} />}
    </Btn>
  );
}

type CreateForm = {
  company_name: string;
  contact_name: string;
  email: string;
  contractor_types: string[];
  vendor_number: string;
  contract_numbers: string[];
  cluster_region: string;
  station: string;
};

const EMPTY_CREATE_FORM: CreateForm = {
  company_name: '',
  contact_name: '',
  email: '',
  contractor_types: [],
  vendor_number: '',
  contract_numbers: [],
  cluster_region: '',
  station: '',
};

function CreateCompanyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { settings } = usePortalSettings();
  const [form, setForm] = useState<CreateForm>(EMPTY_CREATE_FORM);
  const [inviteAfter, setInviteAfter] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const contractorTypes = contractorTypeOptions(settings);
  const clusterRegions = clusterRegionOptions(settings);
  const stations = stationOptions(settings);

  function field<K extends keyof CreateForm>(key: K, value: CreateForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        company_name: form.company_name,
        contact_name: form.contact_name,
        email: form.email,
      };
      payload.cluster_region = form.cluster_region;
      payload.station = form.station;
      const vendorNumberTrimmed = form.vendor_number.trim();
      if (vendorNumberTrimmed.length > 0) payload.vendor_number = vendorNumberTrimmed;
      payload.contractor_types = form.contractor_types;
      if (form.contract_numbers.length > 0) payload.contract_numbers = form.contract_numbers;
      const { company } = await adminApi.post<{ company: Company }>('/api/companies', payload);
      if (inviteAfter) {
        await adminApi.post(`/api/companies/${company.id}/invite`).catch((e) => {
          throw e instanceof ApiError ? e : new Error('Created, but invite failed');
        });
      }
      await qc.invalidateQueries({ queryKey: ['companies'] });
      setForm(EMPTY_CREATE_FORM);
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New vendor"
      size="lg"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            onClick={submit}
            loading={submitting}
            disabled={
              !form.company_name ||
              !form.email ||
              form.contractor_types.length === 0 ||
              !form.cluster_region ||
              !form.station
            }
          >
            Create{inviteAfter ? ' & invite' : ''}
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Vendor legal name" required>
            <TextInput
              value={form.company_name}
              onChange={(e) => field('company_name', e.target.value)}
            />
          </Field>
          <Field label="Contact name" required>
            <TextInput
              value={form.contact_name}
              onChange={(e) => field('contact_name', e.target.value)}
            />
          </Field>
          <Field label="Contact email" required>
            <TextInput
              type="email"
              value={form.email}
              onChange={(e) => field('email', e.target.value)}
            />
          </Field>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="mb-3 text-sm font-semibold text-slate-700">
            Pre-filled vendor information
          </div>
          <p className="mb-3 text-xs text-slate-500">
            These values appear pre-filled on the vendor&apos;s questionnaire when they accept the
            invite. Contractor type, cluster region, and station are required.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label={companyInfoLabel(settings, 'contractorType')}
              required
              hint={
                contractorTypes.length === 0
                  ? 'Add contractor types in Settings to populate these options.'
                  : undefined
              }
            >
              <MultiSelect
                label={companyInfoLabel(settings, 'contractorType')}
                values={contractorTypes}
                selected={form.contractor_types}
                onChange={(next) => field('contractor_types', next)}
              />
            </Field>
            <Field label={companyInfoLabel(settings, 'vendorNumber')}>
              <TextInput
                value={form.vendor_number}
                onChange={(e) => field('vendor_number', e.target.value)}
              />
            </Field>
            <Field label={companyInfoLabel(settings, 'contractNumber')}>
              <StringListEditor
                value={form.contract_numbers}
                onChange={(list) => field('contract_numbers', list)}
                itemLabel="contract number"
                addLabel="Add contract number"
                maxLength={255}
              />
            </Field>
            <Field
              label={companyInfoLabel(settings, 'clusterRegion')}
              required
              hint={
                clusterRegions.length === 0
                  ? 'Add cluster regions in Settings to populate this dropdown.'
                  : undefined
              }
            >
              <Select
                value={form.cluster_region}
                onChange={(e) => field('cluster_region', e.target.value)}
                disabled={clusterRegions.length === 0}
              >
                <option value="">— Select —</option>
                {clusterRegions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label={companyInfoLabel(settings, 'station')}
              required
              hint={
                stations.length === 0
                  ? 'Add stations in Settings to populate this dropdown.'
                  : undefined
              }
            >
              <Select
                value={form.station}
                onChange={(e) => field('station', e.target.value)}
                disabled={stations.length === 0}
              >
                <option value="">— Select —</option>
                {stations.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={inviteAfter}
            onChange={(e) => setInviteAfter(e.target.checked)}
          />
          Send invitation immediately
        </label>
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}
